import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import { getTabLink } from "../extensions/helpers";
import { CarouselFile, CarouselFileType } from "../types/CarouselFile";
import { useShopLink } from "../hooks/useShopLink";
import VideoPlayer from "./VideoPlayer";

type FileCarouselProps = {
  files: CarouselFile[];
  interval?: number;
  arrowsDisabled?: boolean;
  maxSmallWidth?: number;
  customIndicators?: React.ReactNode;
  customIndicatorsDisabled?: boolean;
};

const FileCarousel = ({
  files,
  interval = 5000,
  arrowsDisabled = true,
  maxSmallWidth = 1000,
  customIndicators,
  customIndicatorsDisabled = true,
}: FileCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const swipeDirectionRef = useRef<"h" | "v" | null>(null);

  const currentFile = useMemo(() => files[currentIndex], [files, currentIndex]);

  // Resolved from the live catalog, so renaming the top-level category in
  // Square moves the "Shop Now" button instead of breaking it.
  const shopLink = useShopLink();

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % files.length);
  }, [files.length]);

  const updateCurrentIndex = useCallback((index: number) => {
    clearTimer();
    setCurrentIndex(index);
  }, []);

  // Setup auto-advance for images only
  useEffect(() => {
    if (currentFile.fileType !== CarouselFileType.Video) {
      const delay = currentFile.interval ?? interval;
      timerRef.current = setTimeout(() => {
        goToNext();
      }, delay);
    }

    return () => clearTimer();
  }, [currentIndex, currentFile, interval, goToNext]);

  const handlePrev = useCallback(() => {
    updateCurrentIndex((currentIndex - 1 + files.length) % files.length);
  }, [updateCurrentIndex, currentIndex, files.length]);

  const handleNext = useCallback(() => {
    updateCurrentIndex((currentIndex + 1) % files.length);
  }, [updateCurrentIndex, currentIndex, files.length]);

  const handleDotClick = (index: number) => {
    updateCurrentIndex(index);
  };

  // Lock swipe direction on first movement so a diagonal touch doesn't
  // simultaneously scroll the page and advance the carousel.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
      swipeDirectionRef.current = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - touchStartXRef.current;
      const dy = e.touches[0].clientY - touchStartYRef.current;
      if (!swipeDirectionRef.current) {
        swipeDirectionRef.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
      if (swipeDirectionRef.current === "h") {
        e.preventDefault(); // block page scroll on horizontal swipe
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - touchStartXRef.current;
      if (swipeDirectionRef.current === "h" && Math.abs(dx) > 50) {
        dx < 0 ? handleNext() : handlePrev();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleNext, handlePrev]);

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {!arrowsDisabled && (
        <button className="carousel-button prev" onClick={handlePrev}>
          <img
            src="/images/arrow-left.png"
            alt="Prev"
            className="w-4 h-6 ml-3 bg-opacity-50"
          />
        </button>
      )}

      {currentFile.fileType === CarouselFileType.Video ? (
        <VideoPlayer
          src={currentFile.path}
          smallSrc={currentFile.smallPath ?? ""}
          poster={currentFile.poster}
          smallPoster={currentFile.smallPoster}
          className="carousel-image-full"
          smallClassName="carousel-image-scaled-down"
          onEnded={handleNext}
        />
      ) : (
        <picture className="w-full h-full">
          <source
            srcSet={currentFile.smallPath}
            media={`(max-width: ${maxSmallWidth}px)`}
          />
          <img
            src={currentFile.path}
            alt={`Slide ${currentIndex + 1}`}
            className={currentFile.className ?? "carousel-image-scaled-down"}
          />
        </picture>
      )}

      {(currentFile.showShopButton ||
        currentFile.category ||
        currentFile.domainName) && (
        <>
          <img
            src="/images/carousel-overlay.png"
            alt="Overlay"
            className="carousel-overlay"
          />
          <Link
            to={
              currentFile.domainName
                ? getTabLink({ domain: currentFile.domainName })
                : shopLink
            }
            className="carousel-page-section-overlay-button"
          >
            Shop Now
          </Link>
        </>
      )}

      {!arrowsDisabled && (
        <button className="carousel-button next" onClick={handleNext}>
          <img
            src="/images/arrow-right.png"
            alt="Next"
            className="w-4 h-6 ml-5 bg-opacity-50"
          />
        </button>
      )}

      {!customIndicatorsDisabled &&
        (customIndicators ?? (
          <div className="carousel-indicators">
            {files.map((_, index) => (
              <span
                key={index}
                className={`carousel-indicator ${currentIndex === index ? "active" : ""}`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        ))}
    </div>
  );
};

export default FileCarousel;
